import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { useAuthService } from "../shared/authservice";
import { AuthResponse, AuthUser } from "../shared/types";

const authContext = createContext<{
    user: AuthUser | null;
    errors: string[];
    loading: boolean;
    login: (email: string, password: string, rememberMe: boolean) => void;
    logout: () => void;
    isloggedIn: boolean;
}>({
    user: null,
    errors: [],
    loading: false,
    login: () => { },
    logout: () => { },
    isloggedIn: false,
});

function useProviderAuth() {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();
    const authService = useAuthService();

    const [isloggedIn, setIsLoggedIn] = useState(() =>
        authService.isAuthenticated()
    );

    useEffect(() => {
        setIsLoggedIn(authService.isAuthenticated());
        if (isloggedIn && user === null) {
            fetchUser().then(setUser);
        } else if (!isloggedIn && user !== null) {
            setUser(null);
        }
    }, [authService.isAuthenticated()]);

    async function login(email: string, password: string, rememberMe: boolean) {
        setLoading(true);
        setErrors([]);
        let device_name = navigator.userAgent;
        try {
            let response = await apiService.post('/auth/login', { email, password, device_name });
            if (!response.ok) {
                setErrors([
                    'Login failed. Please check your email and password.',
                ]);
                return;
            }
            let data: AuthResponse = await response.json();

            let token = data.token;
            authService.setAuthToken(token, rememberMe);
            let user = await fetchUser();
            setUser(user);
            setIsLoggedIn(true);
        } catch (error) {
            console.error(error);
            setErrors([
                'Login failed. Please check your email and password.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    async function fetchUser(): Promise<AuthUser | null> {
        setLoading(true);
        let response = await apiService.get('/user');
        if (!response.ok) {
            return null;
        }
        return response.json();
    }

    function logout() {
        setLoading(true);
        setErrors([]);
        authService.clearAuthToken();
        setUser(null);
        setIsLoggedIn(false);
        setLoading(false);
    }

    return {
        user,
        errors,
        loading,
        login,
        logout,
        isloggedIn,
    }
}

export function ProviderAuth({ children }: { children: React.ReactNode }) {
    const auth = useProviderAuth();
    return <authContext.Provider value={auth}> {children}</authContext.Provider>;
}

export const useAuth = () => {
    return useContext(authContext);
}